import InterfaceGroupSelector from 'app/components/interfaceGroup/InterfaceGroupSelector';
import { Box, Button, Flex, Heading, Switch, Text } from 'core/components';
import Table from 'core/components/HTMLTable';
import {
  Checkbox,
  CheckboxGroup,
  Field,
  Form,
  FormDialog,
  InputGroup,
  Select,
  Switch as FormSwitch,
  RadioGroup
} from 'core/form';
import Duration from 'core/form/components/Duration';
import { computed, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { opacify } from 'polished';
import React, { Component } from 'react';
import {
  DEFAULT_GNMI_INTERVAL,
  ESSENTIALS_FEATURE_FLAG,
  fastMeasurements,
  MINIMUM_GNMI_INTERVAL,
  MINIMUM_SNMP_INTERVAL
} from '@kentik/ui-shared/nms/deviceMonitoringTemplates';
import styled from 'styled-components';
import { COMMON_MEASUREMENTS } from './monitoringTemplateConstants';
import { createMonitoringTemplateFields, createPollingSnmpDefaults } from './monitoringTemplateSharedConfig';

const OPT_MODE = {
  ONLY_SELECTED: 'only-selected',
  SELECTIONS_PLUS_ESSENTIALS: 'selections-plus-essentials',
  EVERYTHING: 'everything'
};

const userEditablePollingSnmpFieldConfigs = createPollingSnmpDefaults();
const defaultPollingSnmpFieldConfigs = createPollingSnmpDefaults();

const fields = createMonitoringTemplateFields({
  runOnlyListedReportsField: {
    label: 'Future report handling',
    options: [
      { label: 'Include new reports automatically', value: false },
      { label: 'Run only these reports', value: true }
    ],
    defaultValue: false
  },
  optModeField: {
    label: 'Measurement Opt-Out Options',
    defaultValue: OPT_MODE.ONLY_SELECTED,
    options: [
      { label: "Custom - Only the measurements I've chosen", value: OPT_MODE.ONLY_SELECTED },
      {
        label: 'Curated - Kentik baseline plus my selections',
        value: OPT_MODE.SELECTIONS_PLUS_ESSENTIALS
      },
      { label: 'Everything - Collect all available measurements', value: OPT_MODE.EVERYTHING }
    ]
  },
  userEditablePollingSnmpFieldConfigs,
  defaultPollingSnmpFieldConfigs
});

/**
settings schema
{
    "flow-mode": "standard" | "minimum", // minimum includes only metadata, standard also cpu, mem, interface stats
    "run-only-listed-reports": boolean, // if true, only reports that are explicitly listed in the template will be run
    "intervals": { // default intervals, can be overridden in measurements
        "fastMetrics": "1m",
        "slowMetrics": "5m",
        "metadata": "4h"
    },
    "measurements": { // all measurements with all metrics are enabled with above intervals by default
        "/system/cpus": {
            "interval": "5m",
            "st_interval": "1m",
            "enabled": true,
            "metrics": {
                "total/instant": { "enabled": true }
                "ssCpuRawIdle": { "enabled": false }
            }
        }
    },
    "polling": { // Optional
      "snmp": {
        "retries": 10,
        "timeout": "5s",
        "max-oids": 10,
        "max-repetitions": 10,
        "workers": 10
      }
    }
}
*/

const options = { name: 'Monitoring Profile' };

const HR = styled.hr`
  margin-top: 16px;
  margin-bottom: 16px;
  border-color: ${({ theme }) => opacify(0.18, theme.borderColors.thin)};
  border-style: solid;
  border-width: 1px 0 0;
`;

function measurementPath(measurement) {
  if (measurement.includes('.')) {
    // use square brackets to handle measurements that have dots in their name
    // TODO this notation doesn't work in validatorjs, so these fields don't get validated
    return `settings.measurements['${measurement}']`;
  }
  return `settings.measurements.${measurement}`;
}

@Form({ fields, options })
@inject('$metrics', '$auth')
@observer
export default class MonitoringTemplateForm extends Component {
  state = {
    advancedMode: false,
    interfaceSelectorOpen: false,
    showStInterval: false,
    expandedMeasurements: {},
    measurementFilter: ''
  };

  get uniqueNameRule() {
    const { model } = this.props;
    return `not_in:${model.collection.models.filter((m) => m !== model).map((m) => m.get('name'))}`;
  }

  get isFlowTemplate() {
    const { model, templateType } = this.props;
    return model?.get('template_type') === 'FLOW' || templateType === 'FLOW';
  }

  get allowedMeasurementsForFlow() {
    return ['/system/cpus', '/system/memory', '/interfaces/counters'];
  }

  get isReadOnly() {
    const { readOnly, model } = this.props;
    return readOnly || model?.isPreset;
  }

  get measurements() {
    const { $metrics } = this.props;
    const allMeasurements = $metrics.availableMetricsCollection.models.filter((model) => {
      const measurement = model.get('measurement') || '';
      return !measurement.startsWith('/kentik/') && !measurement.startsWith('kentik/');
    });

    // For FLOW templates, only allow specific measurements
    if (this.isFlowTemplate) {
      return allMeasurements.filter((model) => this.allowedMeasurementsForFlow.includes(model.get('measurement')));
    }

    return allMeasurements;
  }

  @computed
  get essentialMeasurements() {
    return this.measurements.filter((m) => {
      const tags = m.get('tags');
      return Array.isArray(tags) && tags.includes('essential');
    });
  }

  constructor(props) {
    super(props);
    const { form, model, templateType } = this.props;
    const { measurements } = this;
    const settings = model.get('settings');

    // Set template_type if creating new template
    if (model.isNew && templateType) {
      model.set('template_type', templateType);
    }
    // For FLOW templates, set default intervals to 5 minutes metrics, 3h for metadata
    const { isFlowTemplate } = this;
    let fastInterval;
    let slowInterval;

    if (isFlowTemplate) {
      const defaultInterval = '5m';
      const defaultMetadataInterval = '3h';
      form.setValue('template_type', 'FLOW');
      form.setValue('settings.intervals.fastMetrics', defaultInterval);
      form.setValue('settings.intervals.slowMetrics', defaultInterval);
      form.setValue('settings.intervals.metadata', defaultMetadataInterval);
      fastInterval = defaultInterval;
      slowInterval = defaultInterval;
    } else {
      fastInterval = form.getValue('settings.intervals.fastMetrics');
      slowInterval = form.getValue('settings.intervals.slowMetrics');
    }

    let showStInterval = false;

    measurements.forEach((m) => {
      const name = m.get('measurement');
      const metrics = m.get('storage').Metrics || {};
      const isFast = fastMeasurements.includes(name);
      const defaultInterval = isFast ? fastInterval : slowInterval;

      const measurementFields = {
        [`${measurementPath(name)}.enabled`]: { label: name, defaultValue: true },
        [`${measurementPath(name)}.interval`]: {
          rules: ['validDuration', `minDuration:${MINIMUM_SNMP_INTERVAL}`],
          defaultValue: defaultInterval
        },
        [`${measurementPath(name)}.st_interval`]: {
          rules: ['validDuration', `minDuration:${MINIMUM_GNMI_INTERVAL}`],
          defaultValue: DEFAULT_GNMI_INTERVAL
        }
      };

      Object.entries(metrics).forEach(([metric, data]) => {
        measurementFields[`${measurementPath(name)}.metrics.${metric}.enabled`] = {
          label: data.Label || metric,
          defaultValue: true
        };
      });

      showStInterval =
        showStInterval ||
        (settings.measurements?.[name]?.st_interval &&
          settings.measurements[name].st_interval !== DEFAULT_GNMI_INTERVAL);

      form.addFields(measurementFields);
    });

    // re-set model to init new fields
    form.setModel(model);

    // Infer the local UI mode from canonical collection settings.
    // Uses init() to keep the field clean (dirty=false) until the user changes it.
    let resolvedOptMode = OPT_MODE.ONLY_SELECTED;
    const hasEssentialTag = Array.isArray(settings.tags) && settings.tags.includes('essential');
    const hasNoRestrictions = !settings['run-only-listed-reports'] && !settings.measurements;

    if (hasEssentialTag) {
      resolvedOptMode = OPT_MODE.SELECTIONS_PLUS_ESSENTIALS;
    } else if (hasNoRestrictions) {
      resolvedOptMode = OPT_MODE.EVERYTHING;
    }

    if (resolvedOptMode) {
      const optModeField = form.getField('measurement.optMode');
      if (optModeField) {
        optModeField.init(resolvedOptMode);
      }
    }

    // When run-only-listed-reports is true, measurements absent from settings.measurements
    // were intentionally excluded — treat them as disabled rather than defaulting to enabled.
    // helps checkboxes match what was actually saved for existing templates.
    const runOnlyListed = settings['run-only-listed-reports'] === true;
    const essentialNames = new Set(this.essentialMeasurements.map((m) => m.get('measurement')));

    if (runOnlyListed) {
      measurements.forEach((m) => {
        const name = m.get('measurement');
        const keepEnabledForEssentialsOptMode =
          resolvedOptMode === OPT_MODE.SELECTIONS_PLUS_ESSENTIALS && essentialNames.has(name);

        if (!settings.measurements?.[name] && !keepEnabledForEssentialsOptMode) {
          const enabledField = form.getField(`${measurementPath(name)}.enabled`);
          if (enabledField) {
            enabledField.init(false);
          }
        }
      });
    }

    this.state.showStInterval = showStInterval;

    this.syncSimpleMeasurementSelectionsFromEnabledFields();
  }

  syncSimpleMeasurementSelectionsFromEnabledFields = () => {
    const { form } = this.props;
    const { measurements } = this;

    form.getMatchingFields('simple.measurements').forEach((simpleField) => {
      const values = simpleField.options
        .map((option) => option.value)
        .filter((optionValue) => {
          if (optionValue.endsWith('*')) {
            const search = optionValue.slice(0, -1);
            return measurements
              .filter((m) => m.get('measurement').startsWith(search))
              .some((m) => form.getValue(`${measurementPath(m.get('measurement'))}.enabled`) !== false);
          }

          return form.getValue(`${measurementPath(optionValue)}.enabled`) !== false;
        });

      simpleField.init(values);
    });
  };

  getMeasurementMetricsByName = (measurementName) => {
    const measurementModel = this.measurements.find((m) => m.get('measurement') === measurementName);
    return measurementModel?.get('storage')?.Metrics || {};
  };

  setMeasurementMetricsEnabled = (
    measurementName,
    enabled,
    metrics = this.getMeasurementMetricsByName(measurementName)
  ) => {
    const { form } = this.props;

    Object.keys(metrics).forEach((metric) => {
      const metricField = form.getField(`${measurementPath(measurementName)}.metrics.${metric}.enabled`);
      if (metricField) {
        metricField.setValue(enabled);
      }
    });
  };

  setMeasurementAndMetricsEnabled = (measurementName, enabled, metrics) => {
    const { form } = this.props;
    const enabledField = form.getField(`${measurementPath(measurementName)}.enabled`);

    if (enabledField) {
      enabledField.setValue(enabled);
    }

    this.setMeasurementMetricsEnabled(measurementName, enabled, metrics);
  };

  setOptionMeasurementsEnabled = (optionValue, enabled) => {
    const { measurements } = this;

    if (optionValue.endsWith('*')) {
      const search = optionValue.slice(0, -1);
      measurements
        .filter((m) => m.get('measurement').startsWith(search))
        .forEach((m) => {
          const measurementName = m.get('measurement');
          const metrics = m.get('storage').Metrics || {};
          this.setMeasurementAndMetricsEnabled(measurementName, enabled, metrics);
        });
      return;
    }

    this.setMeasurementAndMetricsEnabled(optionValue, enabled);
  };

  ensureEssentialsMeasurementsChecked = () => {
    runInAction(() => {
      this.essentialMeasurements.forEach((m) => {
        const measurementName = m.get('measurement');
        const metrics = m.get('storage').Metrics || {};
        this.setMeasurementAndMetricsEnabled(measurementName, true, metrics);
      });
    });

    this.syncSimpleMeasurementSelectionsFromEnabledFields();
  };

  handleAdvancedToggle = () => {
    this.setState(({ advancedMode }) => {
      const nextAdvancedMode = !advancedMode;

      // When switching back to basic mode, re-sync simple.measurements checkboxes
      // from the current settings.measurements.*.enabled form field values so that
      // any changes made in advanced mode are reflected in the basic mode checkboxes.
      if (!nextAdvancedMode) {
        this.syncSimpleMeasurementSelectionsFromEnabledFields();
      }

      return { advancedMode: nextAdvancedMode };
    });
  };

  handleOptModeChange = (field, value) => {
    const { form, $auth } = this.props;
    const hasEssentialsPermission = $auth.hasPermission(ESSENTIALS_FEATURE_FLAG, {
      overrideForSudo: false
    });

    if (value === OPT_MODE.EVERYTHING) {
      const { advancedMode } = this.state;
      if (advancedMode) {
        this.handleToggleAllAdvancedMeasurements(true);
      } else {
        this.handleToggleAllBasicMeasurements(true);
      }
    } else if (value === OPT_MODE.SELECTIONS_PLUS_ESSENTIALS) {
      this.ensureEssentialsMeasurementsChecked();
    }

    // if essentials permission is on, sync run-only-listed-reports to match what handleSubmit will send
    if (hasEssentialsPermission) {
      const runOnlyField = form.getField('settings.run-only-listed-reports');
      if (runOnlyField) {
        runOnlyField.setValue(value !== OPT_MODE.EVERYTHING);
      }
    }
  };

  handleIntervalChange = () => {
    const { form } = this.props;
    const { measurements } = this;
    const fastInterval = form.getValue('settings.intervals.fastMetrics');
    const slowInterval = form.getValue('settings.intervals.slowMetrics');

    measurements.forEach((m) => {
      const name = m.get('measurement');
      const isFast = fastMeasurements.includes(name);
      const field = form.getField(`${measurementPath(name)}.interval`);

      if (field.getValue() === field.getInitialValue()) {
        field.init(isFast ? fastInterval : slowInterval);
      }
    });
  };

  handleSimpleMeasurementChange = (field, values) => {
    const { form } = this.props;
    const isOnlySelectedMode = form.getValue('measurement.optMode') === OPT_MODE.ONLY_SELECTED;

    // only allow changes in 'only-selected' mode
    if (!isOnlySelectedMode) {
      return;
    }

    // batch updates to avoid n+1 issues
    runInAction(() => {
      field.options.forEach((option) => {
        const enabled = values.includes(option.value);
        this.setOptionMeasurementsEnabled(option.value, enabled);
      });
    });
  };

  handleInterfaceSelectorOpen = () => {
    this.setState({ interfaceSelectorOpen: true });
  };

  handleInterfaceSelectorClose = () => {
    this.setState({ interfaceSelectorOpen: false });
  };

  handleInterfaceGroupSave = () => {
    const { form } = this.props;
    form.setValue('_interfaceGroup', 'dirty');
    form.validate();
  };

  handleStIntervalChange = (checked) => {
    const { form } = this.props;
    this.setState({ showStInterval: checked });

    if (!checked) {
      // reset st_interval
      form.getMatchingFields(/settings\.measurements.+\.st_interval/).forEach((field) => {
        field.setToDefault({ validate: false });
      });
    }
  };

  handleMeasurementToggle = (name) => {
    this.setState(({ expandedMeasurements }) => ({
      expandedMeasurements: { ...expandedMeasurements, [name]: !expandedMeasurements[name] }
    }));
  };

  handleParentMeasurementChange = (measurementName, metrics) => (field, value) => {
    // batch updates to avoid n+1 issues
    runInAction(() => {
      this.setMeasurementMetricsEnabled(measurementName, value, metrics);
    });
  };

  handleMeasurementFilterChange = (e) => {
    this.setState({ measurementFilter: e.target.value });
  };

  handleToggleMeasurementModels = (measurementModels, enabled) => {
    // batch updates to avoid n+1 issues
    runInAction(() => {
      measurementModels.forEach((m) => {
        const measurementName = m.get('measurement');
        const metrics = m.get('storage').Metrics || {};
        this.setMeasurementAndMetricsEnabled(measurementName, enabled, metrics);
      });
    });

    this.syncSimpleMeasurementSelectionsFromEnabledFields();
  };

  handleToggleSimpleMeasurementFields = (fieldNames, enabled) => {
    const { form } = this.props;

    runInAction(() => {
      fieldNames.forEach((fieldName) => {
        const field = form.getField(fieldName);
        if (!field || !Array.isArray(field.options)) {
          return;
        }

        field.setValue(enabled ? field.options.map((opt) => opt.value) : []);
        field.options.forEach((opt) => {
          this.setOptionMeasurementsEnabled(opt.value, enabled);
        });
      });
    });
  };

  handleToggleAllAdvancedMeasurements = (enabled) => {
    this.handleToggleMeasurementModels(this.filteredMeasurements, enabled);
  };

  handleToggleAllBasicMeasurements = (enabled) => {
    this.handleToggleSimpleMeasurementFields(
      [
        'simple.measurements.system',
        'simple.measurements.interfaces',
        'simple.measurements.hardware',
        'simple.measurements.protocols'
      ],
      enabled
    );
  };

  @computed
  get filteredMeasurements() {
    const { measurements } = this;
    const { measurementFilter } = this.state;

    if (!measurementFilter) {
      return measurements;
    }

    const filterLower = measurementFilter.toLowerCase();
    return measurements.filter((m) => m.get('measurement').toLowerCase().includes(filterLower));
  }

  setSnmpPollingFieldsToDefault = () => {
    const { form } = this.props;

    // Store user-entered values
    form.getField('uiProperties.polling.snmp.max-oids').setValue(form.getValue('settings.polling.snmp.max-oids'));
    form
      .getField('uiProperties.polling.snmp.max-repetitions')
      .setValue(form.getValue('settings.polling.snmp.max-repetitions'));
    form.getField('uiProperties.polling.snmp.timeout').setValue(form.getValue('settings.polling.snmp.timeout'));
    form.getField('uiProperties.polling.snmp.retries').setValue(form.getValue('settings.polling.snmp.retries'));
    form.getField('uiProperties.polling.snmp.workers').setValue(form.getValue('settings.polling.snmp.workers'));

    // Reset field values to default
    form.getMatchingFields(/^settings\.polling\.snmp.*/).forEach((field) => {
      field.setToDefault({ validate: false });
    });
  };

  restoreUserEnteredSnmpPollingValuesFromDefault = () => {
    const { form } = this.props;

    // Restore user-entered values
    form.getField('settings.polling.snmp.max-oids').setValue(form.getValue('uiProperties.polling.snmp.max-oids'));
    form
      .getField('settings.polling.snmp.max-repetitions')
      .setValue(form.getValue('uiProperties.polling.snmp.max-repetitions'));
    form.getField('settings.polling.snmp.timeout').setValue(form.getValue('uiProperties.polling.snmp.timeout'));
    form.getField('settings.polling.snmp.retries').setValue(form.getValue('uiProperties.polling.snmp.retries'));
    form.getField('settings.polling.snmp.workers').setValue(form.getValue('uiProperties.polling.snmp.workers'));
  };

  handleUseSnmpPollingDefaultsChange = ({ value: useNmsDefaults }) => {
    if (useNmsDefaults) {
      this.setSnmpPollingFieldsToDefault();
      return;
    }

    this.restoreUserEnteredSnmpPollingValuesFromDefault();
  };

  applyOnlySelectedMeasurements = (settings) => {
    settings['run-only-listed-reports'] = true;
    // clear tags so that ranger doesnt add essentials via tag
    delete settings.tags;

    if (settings.measurements) {
      const filtered = {};
      Object.entries(settings.measurements).forEach(([key, val]) => {
        if (val && val.enabled) {
          filtered[key] = val;
        }
      });
      settings.measurements = filtered;
    }
  };

  applySelectionsWithEssentials = (settings) => {
    settings['run-only-listed-reports'] = true;
    // set tag so the ranger dynamically resolves essential measurements,
    settings.tags = ['essential'];

    // only include non-essential user selections in measurements
    // essentials are already handled dynamically by the tag, no need to duplicate them.
    const essentialNames = new Set(this.essentialMeasurements.map((m) => m.get('measurement')));

    if (settings.measurements) {
      const filtered = {};
      Object.entries(settings.measurements).forEach(([key, val]) => {
        if (val && val.enabled && !essentialNames.has(key)) {
          filtered[key] = val;
        }
      });
      settings.measurements = filtered;
    }
  };

  applyAllMeasurements = (settings) => {
    settings['run-only-listed-reports'] = false;
    // tags not needed when collecting everything
    delete settings.tags;
    // when everything is selected only keep intervals no measurements needed
    delete settings.measurements;
  };

  handleSubmit = (form, values) => {
    const { model } = form;
    const { $auth } = this.props;
    const hasEssentialsPermission = $auth.hasPermission(ESSENTIALS_FEATURE_FLAG, {
      overrideForSudo: false
    });
    const optModeField = form.getField('measurement.optMode');
    const optMode = optModeField?.getValue();
    const shouldApplyEssentials = hasEssentialsPermission && (model.isNew || optModeField?.dirty);
    const settings = values.settings || {};

    delete settings['opt-mode'];
    values.settings = settings;

    // Preserve legacy collection behavior unless the user explicitly changes the local selection mode.
    if (shouldApplyEssentials) {
      if (optMode === OPT_MODE.ONLY_SELECTED) {
        this.applyOnlySelectedMeasurements(settings);
      } else if (optMode === OPT_MODE.SELECTIONS_PLUS_ESSENTIALS) {
        this.applySelectionsWithEssentials(settings);
      } else if (optMode === OPT_MODE.EVERYTHING) {
        this.applyAllMeasurements(settings);
      }

      // Always attach settings back to values so run-only-listed-reports and any
      // measurement filtering/merging are included in the save payload.
    } else if (hasEssentialsPermission) {
      // optMode wasn't changed — preserve existing tags from the model so they
      // aren't silently dropped by the form serializer (tags is not a form field).
      const modelTags = model.get('settings')?.tags;
      if (Array.isArray(modelTags) && modelTags.length > 0) {
        if (!values.settings) {
          values.settings = {};
        }
        values.settings.tags = modelTags;
      }
    }

    if (model.isNew && model.get('template_type')) {
      values.template_type = model.get('template_type');
    }
    return model.save(values);
  };

  render() {
    const { model, form, isDialog, isOpen, onClose } = this.props;
    const { isReadOnly } = this;

    if (isDialog) {
      const { isFlowTemplate } = this;
      return (
        <FormDialog
          form={form}
          model={model}
          isOpen={isOpen}
          canClickOutsideToClose={false}
          entityName={`${isFlowTemplate ? 'Flow Enrichment' : 'NMS'} Monitoring Profile`}
          onClose={onClose}
          width={750}
          formActionsProps={{
            onSubmit: isReadOnly ? undefined : this.handleSubmit,
            submitText: isReadOnly ? undefined : 'Save',
            cancelText: isReadOnly ? 'Close' : 'Cancel',
            otherActions: !isFlowTemplate ? <>{this.advancedToggleButton}</> : null
          }}
        >
          {isReadOnly && (
            <Box mb={2} p={2} backgroundColor="rgba(92, 112, 128, 0.1)" borderRadius={3}>
              <Text muted>
                <strong>System Profile:</strong> This is a read-only preset profile that cannot be modified.
              </Text>
            </Box>
          )}
          {this.formContent}
        </FormDialog>
      );
    }

    return this.formContent;
  }

  get advancedToggleButton() {
    const { advancedMode } = this.state;
    return (
      <Button onClick={this.handleAdvancedToggle}>
        Switch to <span>{!advancedMode ? 'advanced mode' : 'basic mode'}</span>
      </Button>
    );
  }

  get pollingConfigSection() {
    const { form } = this.props;
    const { isReadOnly } = this;
    const useSnmpPollingDefaults = form.getValue('uiProperties.polling.snmp.useDefaults');

    return (
      <>
        <HR />
        <Heading level={5} mb="4px">
          Polling Options
        </Heading>
        <Text muted>Fine tune how SNMP polling is performed.</Text>
        <Flex mt={2} flexDirection="column" gap={2}>
          <Field
            name="uiProperties.polling.snmp.useDefaults"
            large
            onChange={this.handleUseSnmpPollingDefaultsChange}
            disabled={isReadOnly}
          >
            <FormSwitch switchLabel="Use NMS defaults" />
          </Field>
          <Flex gap={1}>
            <Box flex="1">
              <Field
                name="settings.polling.snmp.max-oids"
                component={InputGroup}
                large
                disabled={isReadOnly || useSnmpPollingDefaults}
              />
            </Box>
            <Box flex="1">
              <Field
                name="settings.polling.snmp.max-repetitions"
                component={InputGroup}
                large
                disabled={isReadOnly || useSnmpPollingDefaults}
              />
            </Box>
          </Flex>
          <Flex gap={1}>
            <Field name="settings.polling.snmp.timeout" large disabled={isReadOnly || useSnmpPollingDefaults}>
              <Duration flex="1" width={undefined} />
            </Field>
            <Box flex="1">
              <Field
                name="settings.polling.snmp.retries"
                component={InputGroup}
                large
                disabled={isReadOnly || useSnmpPollingDefaults}
              />
            </Box>
            <Box flex="1">
              <Field
                name="settings.polling.snmp.workers"
                component={InputGroup}
                large
                disabled={isReadOnly || useSnmpPollingDefaults}
              />
            </Box>
          </Flex>
        </Flex>
      </>
    );
  }

  get formContent() {
    const { advancedMode, interfaceSelectorOpen, showStInterval, measurementFilter } = this.state;
    const { model, isDialog, $auth, form } = this.props;
    const { isReadOnly } = this;
    const { filteredMeasurements } = this;
    const hasEssentialsPermission = $auth.hasPermission(ESSENTIALS_FEATURE_FLAG, {
      overrideForSudo: false
    });
    const isOnlySelectedMode = form.getValue('measurement.optMode') === OPT_MODE.ONLY_SELECTED;
    const showMeasurementSelection = !hasEssentialsPermission || isOnlySelectedMode;
    const hasRunOnlySelectedPermission = $auth.hasPermission('recon.templateRunOnlySelected.enabled', {
      overrideForSudo: false
    });

    let headingText = 'Edit';
    if (model.isNew) {
      headingText = 'Add';
    } else if (isReadOnly) {
      headingText = 'View';
    }
    return (
      <Box>
        {!isDialog && (
          <Flex gap={2} alignItems="center" justifyContent="space-between" mb={2}>
            <Heading level={4} mb={0}>
              <span>{headingText}</span> Monitoring Profile
            </Heading>
            {this.advancedToggleButton}
          </Flex>
        )}

        <Field
          name="name"
          rules={`required|${this.uniqueNameRule}`}
          component={InputGroup}
          large
          disabled={isReadOnly}
        />
        <Field name="description" component={InputGroup} large disabled={isReadOnly} />

        {!this.isFlowTemplate && (
          <>
            <HR />
            <Heading level={5} mb="4px">
              Interface selection
            </Heading>
            <Text muted>Choose which interfaces will be monitored</Text>
            <Box mt={2}>
              <InterfaceGroupSelector
                isOpen={interfaceSelectorOpen}
                interfaceGroup={model.interfaceGroup}
                moduleName={model.moduleName}
                onInterfaceGroupSave={this.handleInterfaceGroupSave}
                onInterfaceGroupDialogClose={this.handleInterfaceSelectorClose}
                staticInterfacesTitle="Static Group"
                showAdminStatus
              />
              <Button
                icon="edit"
                text="Edit Selections"
                onClick={this.handleInterfaceSelectorOpen}
                disabled={isReadOnly}
              />
            </Box>
          </>
        )}

        {this.isFlowTemplate && (
          <>
            <HR />
            <Heading level={5} mb="4px">
              SNMP Polling Intervals
            </Heading>
            <Text muted>
              Basic polling is highly recommended. If basic polling is selected the Universal Agent NMS client will poll
              interface counter readings every 5 minutes and poll descriptions every 3 hours. This polling is required
              for the Kentik Map to properly populate with health statistics and for Connectivity costs to accurately
              reflect common ISP billing intervals. If Minimum polling is selected, interface polling is disabled and
              descriptions are polled every 6 hours.
            </Text>
            <Flex mt={2}>
              <Field name="settings.flow-mode" large disabled={isReadOnly}>
                <RadioGroup containerProps={{ flexDirection: 'column', gap: '8px' }} />
              </Field>
            </Flex>
          </>
        )}

        {/* Flow templates have limited options, do not allow them to edit polling intervals beyond setting the flow-mode */}
        {!advancedMode && !this.isFlowTemplate && (
          <>
            <HR />
            <Heading level={5} mb="4px">
              Polling intervals
            </Heading>
            <Text muted>Determine how often data is collected for specific metrics</Text>
            <Flex gap={2} mt={2} width={600}>
              <Box flex="1">
                <Field
                  name="settings.intervals.fastMetrics"
                  onChange={this.handleIntervalChange}
                  component={Duration}
                  large
                  disabled={isReadOnly}
                />
              </Box>
              <Box flex="1">
                <Field
                  name="settings.intervals.slowMetrics"
                  onChange={this.handleIntervalChange}
                  component={Duration}
                  large
                  disabled={isReadOnly}
                />
              </Box>
              <Box flex="1">
                <Field name="settings.intervals.metadata" component={Duration} large disabled={isReadOnly} />
              </Box>
            </Flex>
          </>
        )}

        {this.pollingConfigSection}

        <HR />

        {/* Do not allow flow templates to edit measurement selection. */}
        {!this.isFlowTemplate && (
          <>
            <Heading level={5} mb="4px">
              Choose which metrics to collect for this profile
            </Heading>

            {hasEssentialsPermission && (
              <Flex alignItems="flex-start" mt={2} style={{ gap: 52 }}>
                <Field
                  name="measurement.optMode"
                  component={RadioGroup}
                  large
                  disabled={isReadOnly}
                  onChange={this.handleOptModeChange}
                />
              </Flex>
            )}

            {!advancedMode && showMeasurementSelection && (
              <>
                {hasEssentialsPermission && (
                  <Flex gap={2} alignItems="center" mt={2} mb={1}>
                    <Button
                      text="Select All"
                      outlined
                      onClick={() => this.handleToggleAllBasicMeasurements(true)}
                      disabled={isReadOnly}
                    />
                    <Button
                      text="Deselect All"
                      outlined
                      onClick={() => this.handleToggleAllBasicMeasurements(false)}
                      disabled={isReadOnly}
                    />
                  </Flex>
                )}
                <Flex gap={52} alignItems="flex-start" mt={2}>
                  <Box>
                    <Field
                      name="simple.measurements.system"
                      onChange={this.handleSimpleMeasurementChange}
                      component={CheckboxGroup}
                      large
                      disabled={isReadOnly}
                    />
                  </Box>
                  <Box>
                    <Field
                      name="simple.measurements.interfaces"
                      onChange={this.handleSimpleMeasurementChange}
                      component={CheckboxGroup}
                      large
                      disabled={isReadOnly}
                    />
                  </Box>
                  <Box>
                    <Field
                      name="simple.measurements.hardware"
                      onChange={this.handleSimpleMeasurementChange}
                      component={CheckboxGroup}
                      large
                      disabled={isReadOnly}
                    />
                  </Box>
                  <Box>
                    <Field
                      name="simple.measurements.protocols"
                      onChange={this.handleSimpleMeasurementChange}
                      component={CheckboxGroup}
                      large
                      disabled={isReadOnly}
                    />
                  </Box>
                </Flex>
              </>
            )}
          </>
        )}
        {advancedMode && showMeasurementSelection && (
          <>
            <Box mt={2}>
              {hasRunOnlySelectedPermission && (
                <Field
                  name="settings.run-only-listed-reports"
                  component={Select}
                  disabled={isReadOnly || hasEssentialsPermission}
                />
              )}
              <Switch
                checked={showStInterval}
                onChange={this.handleStIntervalChange}
                disabled={isReadOnly}
                labelElement={
                  <>
                    Specify gNMI intervals
                    <Text as="div" muted>
                      gNMI collection will be preferred over SNMP where available
                    </Text>
                  </>
                }
              />
              <Flex gap={2} alignItems="center" mt={3} mb={2}>
                <InputGroup
                  leftIcon="search"
                  placeholder="Filter measurements..."
                  value={measurementFilter}
                  onChange={this.handleMeasurementFilterChange}
                  large
                  disabled={isReadOnly}
                  style={{ maxWidth: '400px' }}
                />
                <Button
                  text="Select All"
                  onClick={() => this.handleToggleAllAdvancedMeasurements(true)}
                  outlined
                  disabled={isReadOnly}
                />
                <Button
                  text="Deselect All"
                  onClick={() => this.handleToggleAllAdvancedMeasurements(false)}
                  outlined
                  disabled={isReadOnly}
                />
              </Flex>
              <Table condensed width="auto" mt={2}>
                <thead>
                  <tr>
                    <th style={{ width: 30 }} />
                    <th>Enabled</th>
                    <th>SNMP Interval</th>
                    {showStInterval && <th>gNMI Interval</th>}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th />
                    <th colSpan={2 + (showStInterval ? 1 : 0)}>
                      <Text fontWeight="normal" muted>
                        Common
                      </Text>
                    </th>
                  </tr>
                  {filteredMeasurements
                    .filter((m) => COMMON_MEASUREMENTS.includes(m.get('measurement')))
                    .map(this.createMeasurementRow)}
                  <tr>
                    <th />
                    <th colSpan={2 + (showStInterval ? 1 : 0)}>
                      <Text fontWeight="normal" muted>
                        Less common
                      </Text>
                    </th>
                  </tr>
                  {filteredMeasurements
                    .filter((m) => !COMMON_MEASUREMENTS.includes(m.get('measurement')))
                    .map(this.createMeasurementRow)}
                </tbody>
              </Table>
            </Box>
          </>
        )}
      </Box>
    );
  }

  createMeasurementRow = (measurementModel) => {
    const { form } = this.props;
    const { showStInterval, expandedMeasurements } = this.state;
    const { isReadOnly } = this;
    const name = measurementModel.get('measurement');
    const metrics = measurementModel.get('storage').Metrics || {};
    const expanded = !!expandedMeasurements[name];
    const enabledMeasurement = form.getValue(`${measurementPath(name)}.enabled`);
    const enabledMetrics = form
      .getMatchingFields(`${measurementPath(name)}.metrics`)
      .filter((f) => f.getValue()).length;
    const numMetrics = Object.keys(metrics).length;
    const indeterminate = enabledMeasurement && enabledMetrics < numMetrics;
    return (
      <tr key={measurementModel.id}>
        <td style={{ padding: 0 }}>
          <Button
            onClick={() => this.handleMeasurementToggle(name)}
            icon={!expanded ? 'caret-right' : 'caret-down'}
            minimal
            title="Expand"
          />
        </td>
        <td>
          <Field
            name={`${measurementPath(name)}.enabled`}
            showLabel={false}
            mb={0}
            large
            onChange={this.handleParentMeasurementChange(name, metrics)}
            disabled={isReadOnly}
          >
            <Checkbox
              indeterminate={indeterminate}
              label={
                <>
                  <Text>{name}</Text>
                  {indeterminate && (
                    <Text muted fontSize="0.8em" pt="0.2em" pl="1.5ex">
                      {enabledMetrics} of {numMetrics} <span>{enabledMetrics === 1 ? 'metric' : 'metrics'}</span>
                    </Text>
                  )}
                </>
              }
            />
          </Field>
          {expanded && (
            <Box pt="10px" pl="20px">
              {Object.keys(metrics).map((metric) => (
                <Field
                  key={metric}
                  name={`${measurementPath(name)}.metrics.${metric}.enabled`}
                  component={Checkbox}
                  showLabel={false}
                  mb={1}
                  large
                  disabled={isReadOnly}
                />
              ))}
            </Box>
          )}
        </td>
        <td>
          <Field name={`${measurementPath(name)}.interval`} component={Duration} mb={0} disabled={isReadOnly} />
        </td>
        {showStInterval && (
          <td>
            <Field name={`${measurementPath(name)}.st_interval`} component={Duration} mb={0} disabled={isReadOnly} />
          </td>
        )}
      </tr>
    );
  };
}
